import { FC, Fragment, useEffect, useMemo } from "react";
import { MarkerF, PolylineF } from "@react-google-maps/api";
import { types, utils } from "@vilocnv/allsetra-core";
import { isEmpty } from "lodash";
import { isValidMapCoordinate } from "app/data/helpers/mapHelpers";
import { ObjectRideModeEnum } from "app/data/helpers/objectRideHelpers";
import { LAST_TRIPS_PANEL_WIDTH } from "../../constants/lastTrips.constants";

type MigratedRide = types.IObjectRide & {
  isMigrated?: boolean;
  correctedStartLatitude?: number | null;
  correctedStartLongitude?: number | null;
  correctedEndLatitude?: number | null;
  correctedEndLongitude?: number | null;
};

interface LastTripsRouteOverlayProps {
  mapRef?: google.maps.Map;
  selectedTrip: MigratedRide | null;
  rideLocations: types.IObjectRideLocation[];
}

const ROUTE_COLOR = "#498feb";

const LastTripsRouteOverlay: FC<LastTripsRouteOverlayProps> = ({
  mapRef,
  selectedTrip,
  rideLocations
}) => {
  const startIcon = require("assets/icons/common/startLocation.svg").default;
  const stopIcon = require("assets/icons/common/endLocation.svg").default;

  const pathPoints = useMemo(() => {
    if (!selectedTrip) return [];

    // Private trips keep a selectedTrip for panel state, often with (0,0)
    // placeholders — never draw or pan to those endpoints.
    if (selectedTrip.rideMode === ObjectRideModeEnum.PRIVATE) {
      return [];
    }

    if (!isEmpty(rideLocations) && !selectedTrip.isMigrated) {
      const validPoints = rideLocations
        .filter((item) => isValidMapCoordinate(item.latitude, item.longitude))
        .map((item) => ({
          lat: item.latitude as number,
          lng: item.longitude as number
        }));

      // Fall through to corrected/raw start-end when GPS points are all invalid.
      if (validPoints.length >= 2) {
        return validPoints;
      }
    }

    const startLat =
      selectedTrip.correctedStartLatitude ?? selectedTrip.startLatitude;
    const startLng =
      selectedTrip.correctedStartLongitude ?? selectedTrip.startLongitude;
    const endLat =
      selectedTrip.correctedEndLatitude ?? selectedTrip.endLatitude;
    const endLng =
      selectedTrip.correctedEndLongitude ?? selectedTrip.endLongitude;

    if (
      isValidMapCoordinate(startLat, startLng) &&
      isValidMapCoordinate(endLat, endLng)
    ) {
      return [
        { lat: startLat as number, lng: startLng as number },
        { lat: endLat as number, lng: endLng as number }
      ];
    }

    return [];
  }, [rideLocations, selectedTrip]);

  const endpointMarkers = useMemo(() => {
    if (!selectedTrip || pathPoints.length < 2) return [];

    return [pathPoints[0], pathPoints[pathPoints.length - 1]];
  }, [pathPoints, selectedTrip]);

  useEffect(() => {
    if (!mapRef || !pathPoints.length) return;

    const bounds = new google.maps.LatLngBounds();
    pathPoints.forEach((point) => bounds.extend(point));
    mapRef.fitBounds(bounds, {
      top: 50,
      right: LAST_TRIPS_PANEL_WIDTH + 50,
      bottom: 50,
      left: 50
    });
  }, [mapRef, pathPoints, selectedTrip?.uniqueId]);

  if (!selectedTrip || !pathPoints.length) return null;

  const darkenedColor = utils.darkenColor(ROUTE_COLOR, 20);

  return (
    <Fragment>
      <PolylineF
        path={pathPoints}
        options={{
          strokeColor: darkenedColor,
          strokeWeight: 16,
          zIndex: 1,
          strokeOpacity: 1
        }}
      />
      <PolylineF
        path={pathPoints}
        options={{
          strokeColor: ROUTE_COLOR,
          strokeWeight: 10,
          zIndex: 2
        }}
      />
      {endpointMarkers.map((point, index) => (
        <MarkerF
          key={`${selectedTrip.uniqueId}-${index}`}
          position={point}
          icon={{
            url: index === 0 ? startIcon : stopIcon,
            scaledSize: utils.getMapSize(32, 32) as google.maps.Size,
            anchor: new google.maps.Point(25, index === 0 ? 15 : 25)
          }}
        />
      ))}
    </Fragment>
  );
};

export default LastTripsRouteOverlay;
